import React, { useEffect, useState } from "react";
import LoggedInLayout from "../../layout/LoggedInLayout";
import {
    Box,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
} from "@mui/material";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import Chatbot from "../common/Chatbot";
import httpcommon from "../../helper/httpcommon";
import { useNavigate } from "react-router-dom";
import CommonButtonActionButton from "../common/CommonButtonActionButton";

const AssessmentTest = ({ defaultTest, onComplete ,content=null}) => {
    const navigate = useNavigate();

    const [triggerNewConversion, setTriggerNewConversion] = useState(true);
    const [student, setStudent] = useState(null);
    const [isLoading, setLoading] = useState(true);
    const [selectSubject, setSelectSubject] = useState(defaultTest);
    const [openConfirm, setOpenConfirm] = useState(false);
    const [testStarted, setTestStarted] = useState(true);

    const fetchStudent = async () => {
        try {
            setLoading(true);
            const response = await httpcommon.get("school/student/profile/");
            if (response.status === 200) {
                const data = response.data;
                setStudent({
                    school_name: data.school || "",
                    board: data.board,
                    board_name: data.board || "",
                    class: data.student_class || "",
                    section: data.section || "A",
                    roll_number: data.roll_number || "1",
                    birth_date: data.birth_date || "",
                    contact_number: data.contact_number || "",
                    address: data.address || "",
                    student_name: data.user_name || "",
                    avatar: data.avatar || "",
                    profileCompletion: data.profile_completion || 0,
                });
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setSelectSubject(defaultTest);
        fetchStudent();
        // Entering a test step always begins a clean test automatically.
        setTriggerNewConversion(true);
        setTestStarted(true);
    }, [defaultTest]);

    // âœ… Confirm new conversation
    const handleConfirmNewConversation = () => {
        setTestStarted(true);
        if (triggerNewConversion == null) {
            setTriggerNewConversion(true);
        } else {
            setTriggerNewConversion(!triggerNewConversion);
        }
        setOpenConfirm(false);
    };

    return (
        <Box
            sx={{
                backgroundColor: "transparent",
                borderRadius: "18px",
                width: "100%",
            }}
        >
            <Box
                sx={{
                    background: "linear-gradient(135deg, #EAF6F7 0%, #F4FAFB 100%)",
                    border: "1px solid #D6EAEC",
                    px: { xs: 2, sm: 2.5 },
                    py: { xs: 1.75, sm: 2 },
                    borderRadius: "16px",
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", sm: "center" },
                    gap: 1.5,
                }}
            >
                <Box sx={{ minWidth: 0 }}>{content}</Box>

                {/* Start / restart the test (same behaviour as before) */}
                <Button
                    onClick={() => handleConfirmNewConversation()}
                    startIcon={<PlayArrowRoundedIcon />}
                    sx={{
                        flexShrink: 0,
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: 13.5,
                        color: "#fff",
                        backgroundColor: "#F6A03A",
                        borderRadius: "10px",
                        px: 2.5,
                        py: 0.9,
                        boxShadow: "0 8px 18px rgba(246,160,58,0.30)",
                        transition: "transform .18s ease, background-color .18s ease",
                        "&:hover": {
                            backgroundColor: "#E8912B",
                            transform: "translateY(-1px)",
                        },
                    }}
                >
                    Start New Test
                </Button>
            </Box>

            {/* Do not create/fetch a test session until the student explicitly starts one. */}
            {testStarted ? (
                <Chatbot
                    variant="assessment"
                    onComplete={onComplete}
                    selectSubject={selectSubject}
                    student={student}
                    triggerNewConversion={triggerNewConversion}
                    deferInitialLoad
                />
            ) : (
                <Box sx={{ mt: 2, minHeight: 190, display: 'grid', placeItems: 'center', border: '1px solid #E7EAF1', borderRadius: '18px', backgroundColor: '#FCFCFE', color: '#697089', textAlign: 'center', px: 3 }}>
                    <Typography sx={{ fontSize: 14, lineHeight: 1.6 }}>Start a new test when you are ready. Your first question will appear here.</Typography>
                </Box>
            )}

            {/* âœ… Confirmation Dialog */}
            <Dialog
                open={openConfirm}
                onClose={() => setOpenConfirm(false)}
                PaperProps={{ sx: { borderRadius: "18px", p: 1, maxWidth: 380 } }}
            >
                <DialogTitle sx={{ fontWeight: 800, color: "#292D5B" }}>Restart this test?</DialogTitle>

                <DialogContent>
                    <Typography fontSize="14px" sx={{ color: "#3E4A66", lineHeight: 1.7 }}>
                        This will clear your current progress in this section and start it over.
                        Are you sure you want to continue?
                    </Typography>
                </DialogContent>

                <DialogActions sx={{ padding: 2 }}>

                    <Button
                        onClick={() => setOpenConfirm(false)}
                        sx={{
                            borderRadius: "100px",
                            textTransform: "none",
                            fontWeight: 700,
                            color: "#7A8096",
                            px: 2.5,
                        }}
                    >
                        Cancel
                    </Button>

                    <CommonButtonActionButton
                        onClick={handleConfirmNewConversation}
                        variant="contained"
                        sx={{
                            borderRadius: "100px",

                            backgroundColor: "rgba(247, 170, 71, 1)",
                            "&:hover": {
                                backgroundColor: "rgba(247, 170, 71, 1)",
                            },
                        }}
                    >
                        Yes, Start New
                    </CommonButtonActionButton>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AssessmentTest;
